<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { deepEqual } from 'ts-deep-utils'
import { useQuery } from '@/composables/api'
import { useCreditorSettings } from '@/composables/useCreditorSettings'
import { deepToRaw } from '@/utils/vue'
import BDialog from '@/components/base/BDialog/BDialog.vue'
import BSelect from '@/components/base/BSelect/BSelect.vue'
import type { components } from '@/types/_api'

type Creditor = components['schemas']['Creditor']

const STATISTIC_TYPES = ['reminder', 'debt_collection', 'enforcement', 'debt_surveillance', 'amortization'] as const
type StatisticType = (typeof STATISTIC_TYPES)[number]

interface SettingState {
  switch: boolean
  creditors: string[]
}

function normalizeHidden(hidden: unknown): StatisticType[] {
  if (Array.isArray(hidden)) return hidden as StatisticType[]
  if (typeof hidden === 'string') return [hidden as StatisticType]
  return []
}

defineProps<{
  description?: string
}>()

const model = defineModel<boolean>({ default: false })
const { t } = useI18n()
const formId = useId()

const { data: creditorsData, isLoading: isLoadingCreditors } = useQuery(
  '/creditors',
  {
    getAll: true,
    query: {
      where: { is_active: true },
      sort: 'name',
      projection: { name: 1, _id: 1 },
    },
  },
  { enabled: model }
)

const creditors = computed<Creditor[]>(() => creditorsData.value?._items ?? [])
const creditorIds = computed(() => creditors.value.map((c) => c._id))
const hasMultipleCreditors = computed(() => creditors.value.length > 1)

const {
  isLoading: isLoadingCreditorSettings,
  isPending,
  serverCreditorSettings,
  bulkUpdateSettings,
  saveCreditorSettingsImmediately,
} = useCreditorSettings()

const isLoading = computed(() => isLoadingCreditors.value || isLoadingCreditorSettings.value)

const localState = ref({} as Record<StatisticType, SettingState>)
const oldState = ref({} as Record<StatisticType, SettingState>)

function initLocalState() {
  const state = {} as Record<StatisticType, SettingState>

  for (const key of STATISTIC_TYPES) {
    state[key] = { switch: false, creditors: [] }
  }

  for (const creditorId of creditorIds.value) {
    const creditorSettings = serverCreditorSettings(creditorId)
    const hiddenArray = normalizeHidden(creditorSettings?.settings.dashboard_statistics?.hidden)

    for (const key of STATISTIC_TYPES) {
      const isEnabled = !hiddenArray.includes(key)
      if (isEnabled) {
        state[key].creditors.push(creditorId)
        state[key].switch = true
      }
    }
  }

  localState.value = state
  oldState.value = deepToRaw(state)
}

watch(
  [model, isLoading],
  ([isOpen, loading]) => {
    if (isOpen && !loading) {
      initLocalState()
    }
  },
  { immediate: true }
)

function getSettingSwitch(key: StatisticType): boolean {
  return localState.value[key].switch
}

function getSettingCreditors(key: StatisticType): string[] {
  return localState.value[key].creditors
}

const hasChanges = computed(() => {
  return !deepEqual(localState.value, oldState.value)
})

function onToggle(key: StatisticType, newValue: boolean) {
  localState.value[key].switch = newValue
  if (newValue) {
    localState.value[key].creditors = creditorIds.value.slice()
  } else {
    localState.value[key].creditors = []
  }
}

function onCreditorsChange(key: StatisticType, newCreditors: string[]) {
  localState.value[key].creditors = newCreditors
}

// Pre-computed Sets give O(1) lookup per (creditor, stat) check; batching
// yields between chunks so the main thread stays responsive on large accounts.
const SAVE_BATCH_SIZE = 200

async function save() {
  const enabledByStat = {} as Record<StatisticType, Set<string>>
  for (const key of STATISTIC_TYPES) {
    enabledByStat[key] = new Set(localState.value[key].creditors)
  }

  const operations: { creditorId: string; dottedPath: string; value: unknown }[] = []

  for (let i = 0; i < creditorIds.value.length; i += SAVE_BATCH_SIZE) {
    const batch = creditorIds.value.slice(i, i + SAVE_BATCH_SIZE)

    for (const creditorId of batch) {
      const currentSettings = serverCreditorSettings(creditorId)
      const currentHidden = new Set(normalizeHidden(currentSettings?.settings.dashboard_statistics?.hidden))
      const newHidden = new Set<StatisticType>()

      for (const key of STATISTIC_TYPES) {
        if (!enabledByStat[key].has(creditorId)) {
          newHidden.add(key)
        }
      }

      if (currentHidden.symmetricDifference(newHidden).size > 0) {
        operations.push({
          creditorId,
          dottedPath: 'dashboard_statistics.hidden',
          value: Array.from(newHidden).sort(),
        })
      }
    }

    if (i + SAVE_BATCH_SIZE < creditorIds.value.length) {
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  bulkUpdateSettings(operations)

  await saveCreditorSettingsImmediately()
  oldState.value = deepToRaw(localState.value)
  model.value = false
}
</script>

<template>
  <b-dialog
    v-model="model"
    size="medium"
    :confirm-on-close="hasChanges"
  >
    <template #title>{{ t('common.adjust_statistics') }}</template>

    <v-progress-linear
      v-if="isLoading"
      indeterminate
    />

    <v-form
      v-if="!isLoading"
      :id="formId"
      @submit.prevent="save"
    >
      <v-card-text>
        <div class="mb-4">
          {{ description }}
        </div>

        <div
          v-for="(key, index) in STATISTIC_TYPES"
          :key="key"
        >
          <div class="d-flex justify-space-between align-center">
            <label
              :for="`statistics-switch-${key}`"
              class="text-body-large font-weight-medium text-medium-emphasis"
            >
              {{ t(`common.${key}`) }}
            </label>
            <v-switch
              :id="`statistics-switch-${key}`"
              :model-value="getSettingSwitch(key)"
              :disabled="isPending"
              color="brand-primary"
              hide-details
              class="ma-0 flex-grow-0"
              @update:model-value="onToggle(key, $event as boolean)"
            />
          </div>

          <b-select
            v-if="getSettingSwitch(key) && hasMultipleCreditors"
            :model-value="getSettingCreditors(key)"
            :items="creditors"
            :label="t('common.select_creditors')"
            :disabled="isPending"
            item-title="name"
            item-value="_id"
            multiple
            clearable
            class="mt-2"
            @update:model-value="onCreditorsChange(key, $event as string[])"
          />

          <v-divider
            v-if="index < STATISTIC_TYPES.length - 1"
            class="my-4"
          />
        </div>
      </v-card-text>
    </v-form>

    <template #footer="{ closeProps }">
      <v-btn
        :disabled="isPending"
        variant="text"
        color="primary"
        v-bind="closeProps"
      >
        {{ t('common.cancel') }}
      </v-btn>
      <v-btn
        type="submit"
        :form="formId"
        :disabled="!hasChanges"
        :loading="isPending"
        color="primary"
        variant="flat"
      >
        {{ t('common.save') }}
      </v-btn>
    </template>
  </b-dialog>
</template>
